<template>
  <div id="phone-controller">
    <audio ref="audio" autoplay />
    <dial-pad-modal @call="startOutboundCall" @answer="answer" @terminate="terminate" :engaging-call="engagingCall" />
  </div>
</template>

<script>
import { watch, ref } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import JsSIP from 'jssip';
import moment from 'moment';

import { upperSnake } from '@/helpers';
import { CALL_STATUSES, CALL_DIRECTIONS, CALL_LOG_STATUSES, DURATION_FORMAT, CALL_REFS } from '@/consts';

import OrderManagementService from '@/views/Order/OrderManagementService';

import theSea from '@/assets/audio/xoi-the-sea.mp3';
import logo from '@/assets/imgs/logo-circle.png';

import DialPadModal from './DialPadModal';
import phoneControllerServices from './PhoneControllerServices';

if (process.env.VUE_APP_DEBUG) {
  JsSIP.debug.enable('JsSIP:*');
} else {
  JsSIP.debug.disable('JsSIP:*');
}

const callOptions = {
  mediaConstraints: { audio: true, video: false },
  sessionTimersExpires: '180',
  pcConfig: {
    iceCandidatePoolSize: 255,
    iceServers: [
      {
        urls: [
          'stun:stun.l.google.com:19302',
          'stun:stun1.l.google.com:19302',
          'stun:stun2.l.google.com:19302',
          'stun:stun3.l.google.com:19302',
          'stun:stun4.l.google.com:19302'
        ]
      }
    ],
    iceTransportPolicy: 'all',
    rtcpMuxPolicy: 'negotiate'
  }
};

const {
  useGetters: useUserGetters,
  useState: useUserState,
} = createNamespacedHelpers('user');

const {
  useActions: useFlashActions
} = createNamespacedHelpers('flash');

const {
  useState: usePhoneState,
  useActions: usePhoneActions
} = createNamespacedHelpers('phone');

export default {
  name: 'PhoneController',
  components: {
    DialPadModal
  },
  setup(props, { root }) {
    const socket = new JsSIP.WebSocketInterface(process.env.VUE_APP_SIP_SOCKET);
    const ALERT_TIMEOUT = 3000;
    let alertTimeout;

    let iceCandidateTimeout;
    const MAX_ICE_CANDIDATE_TIME = 2000;

    const handleIceTimeout = (candidate) => {
      if (iceCandidateTimeout) {
        clearTimeout(iceCandidateTimeout);
      }

      iceCandidateTimeout = setTimeout(() => {
        candidate.ready();
      }, MAX_ICE_CANDIDATE_TIME);
    };

    const { hasSipAccount } = useUserGetters(['hasSipAccount']);
    const { sip } = useUserState(['sip']);
    const { info, error } = useFlashActions(['info', 'error']);
    const { toggleDialPad, addCall, updateCallId, removeCall, clearRef } = usePhoneActions(['toggleDialPad', 'addCall', 'updateCallId', 'removeCall', 'clearRef']);
    const { connected, registered, remote, volume, calls, ref: callRef } = usePhoneState(['connected', 'registered', 'remote', 'volume', 'calls', 'ref']);
    const {
      toggleConnection,
      toggleRegistration,
      setStatus,
      startOutboundCall,
    } = usePhoneActions([
      'toggleConnection',
      'toggleRegistration',
      'setStatus',
      'startOutboundCall'
    ]);

    const audio = ref(null);
    const engagingCall = ref(null);

    let phone;

    const ringtone = new Audio(theSea);
    ringtone.loop = true;

    const ring = () => {
      if ((ringtone.duration > 0) && !ringtone.paused) {
        return;
      }

      ringtone.currentTime = 0;
      ringtone.play();
    };

    const end = (id) => {
      const i = calls.value.findIndex((c) => c.id === id);
      if (i < 0) return;

      const call = calls.value[i];
      const { startTime, ref: thisCallRef } = call;

      const duration = moment.utc(moment().diff(startTime)).format(DURATION_FORMAT);
      phoneControllerServices.logCall({
        id,
        duration
      });

      if (engagingCall.value?.id === id) {
        engagingCall.value = null;
      }

      removeCall(id);

      if (!calls.value.length) {
        audio.value.srcObject = null;
        ringtone.pause();
      }

      if (thisCallRef?.type) {
        const { type, id: orderId } = thisCallRef;

        switch(type) {
          case CALL_REFS.ORDER:
            OrderManagementService.removeWorkingAgent(orderId);
            break;

          default:
            break;
        }
      }
    };

    const notifyInbound = async (number) => {
      if (Notification.permission !== 'granted') {
        await Notification.requestPermission();
        // only ask once
        if (Notification.permission !== 'granted') {
          return;
        }
      }

      const notification = new Notification(root.$i18n.t('YOURE_HAVING_AN_INBOUND_CALL_ON_CRM', { number }), {
        icon: logo
      });

      notification.onclick = () => {
        if (document.hidden) {
          window.focus();
        }
      };
    };

    const inbound = (data) => {
      const { session } = data;
      const number = data.request.from._uri._user;
      let id = `${data.request.from._uri._user}_${data.request.cseq}`;

      const call = {
        id,
        session,
        startTime: moment(),
        direction: CALL_DIRECTIONS.INBOUND,
        number
      };

      addCall(call);

      phoneControllerServices.logCall({
        cseq: `${data.request.from._uri._user}_${data.request.cseq}`,
        direction: CALL_DIRECTIONS.INBOUND,
        status: CALL_LOG_STATUSES.MISSED,
        extension: sip.value.username,
        remote: data.request.from._uri._user
      }).then((result) => {
        if (result) {
          id = result;
          updateCallId({ number, id });
        }
      });

      session.on('icecandidate', handleIceTimeout);
      session.on('ended', (e) => {
        info(upperSnake(`PHONE_${e.cause}`));
        end(id);
      });
      session.on('failed', (e) => {
        const someonePickedUp = /Call completed elsewhere/.test(e.message?.data);

        if (someonePickedUp) {
          info(root.$i18n.t('SOMEONE_ELSE_PICKED_UP_THE_CALL', { number }));
        } else {
          phoneControllerServices.logCall({
            id,
            status: (e.cause === 'Rejected') ? CALL_LOG_STATUSES.CANCELLED : CALL_LOG_STATUSES.MISSED
          });

          error(upperSnake(`PHONE_${e.cause}`));
        }

        end(id);
      });
      session.on('confirmed', () => {
        ringtone.pause();
        setStatus(CALL_STATUSES.TALKING);
        const receivers = session.connection.getReceivers();
        const [{ track }] = receivers;
        audio.value.srcObject = new MediaStream([track]);

        phoneControllerServices.logCall({
          id,
          status: CALL_LOG_STATUSES.ANSWERED
        });
      });
    };

    const register = ({ username, password }) => {
      const uri = `sip:${username}@${process.env.VUE_APP_SIP_DOMAIN}`;
      const configuration = {
        sockets: [socket],
        uri,
        password,
        contact_uri: `${uri};transport=ws`
      };

      phone = new JsSIP.UA(configuration);

      phone.on('registered', () => {
        if (alertTimeout) clearTimeout(alertTimeout);

        alertTimeout = setTimeout(() => {
          if (!connected.value) {
            info('SUCCESSFULLY_CONNECTED_TO_PHONE_SERVICE');
          }

          toggleConnection(true);
        }, registered.value ? ALERT_TIMEOUT : 0);

        navigator.mediaDevices.getUserMedia({ audio: true });
        toggleRegistration(true);
      });

      phone.on('registrationFailed', () => {
        error('FAILED_TO_CONNECT_TO_PHONE_SERVICE');
        toggleRegistration(false);
      });

      phone.on('disconnected', () => {
        if (registered.value) {
          if (alertTimeout) clearTimeout(alertTimeout);

          alertTimeout = setTimeout(() => {
            if (connected.value) {
              error('PHONE_SERVICE_DISCONNECTED');
            }
          }, ALERT_TIMEOUT);

          toggleConnection(false);
        }
      });

      phone.on('newRTCSession', async (data) => {
        const isInbound = (data.originator === 'remote');

        if (isInbound) {
          inbound(data);
        }
      });

      phone.start();
    };

    const outbound = async (number) => {
      if (calls.value.length) {
        return;
      }

      toggleDialPad(true);

      let id;

      const eventHandlers = {
        connecting() {
          setStatus(CALL_STATUSES.CONNECTING);
        },
        progress() {
          setStatus(CALL_STATUSES.RINGING);
        },
        icecandidate: handleIceTimeout,
        confirmed() {
          setStatus(CALL_STATUSES.TALKING);

          phoneControllerServices.logCall({
            id,
            status: CALL_LOG_STATUSES.ANSWERED
          });
        },
        ended(e) {
          info(upperSnake(`PHONE_${e.cause}`));
          end(id);
        },
        failed(e) {
          phoneControllerServices.logCall({
            id,
            status: CALL_LOG_STATUSES.FAILED
          });

          error(upperSnake(`PHONE_${e.cause}`));
          end(id);
        },
        peerconnection({ peerconnection }) {
          peerconnection.addEventListener('track', ({ track }) => {
            audio.value.srcObject = new MediaStream([track]);
          });
        }
      };

      const options = {
        eventHandlers,
        ...callOptions
      };

      const session = phone.call(`sip:${number}@${process.env.VUE_APP_SIP_DOMAIN}`, options);

      id = `${number}_${session._request.cseq}`;

      const call = {
        id,
        session,
        startTime: moment(),
        direction: CALL_DIRECTIONS.OUTBOUND,
        number,
        ref: callRef.value
      };

      clearRef();

      addCall(call);

      engagingCall.value = call;

      const result = await phoneControllerServices.logCall({
        cseq: `${number}_${session._request.cseq}`,
        direction: CALL_DIRECTIONS.OUTBOUND,
        status: CALL_LOG_STATUSES.MISSED,
        extension: sip.value.username,
        remote: number,
        ref: callRef.value
      });

      id = result ? id : null;

      updateCallId({ number, id });
    };

    const answer = (id) => {
      const call = calls.value.find((c) => c.id === id);
      if (!call) return;

      engagingCall.value = call;

      const { session } = call;

      if (!session) return;
      setStatus(CALL_STATUSES.PICKING_UP);
      session.answer(callOptions);
    };

    const terminate = (id) => {
      const call = calls.value.find((c) => c.id === id);
      if (!call) return;

      const { session } = call;

      if (!session) return;
      session.terminate();
    };

    const disconnect = () => {
      if (phone) {
        phone.stop();
        phone.unregister();
        phone = null;
      }
    };

    watch(hasSipAccount, (val) => {
      if (val) {
        if (!phone) {
          register(sip.value);
        }
      } else {
        disconnect();
      }
    }, {
      immediate: true
    });

    watch(volume, (val) => {
      audio.value.volume = val;
    });

    watch(remote, (val) => {
      if (val) {
        outbound(val);
      }
    });

    watch(calls, (vals) => {
      if (engagingCall.value) return;

      if (!vals.length) {
        setStatus(CALL_STATUSES.IDLE);
      } else {
        const hasInbound = vals.some((val) => (val.direction === CALL_DIRECTIONS.INBOUND));
        if (hasInbound) {
          setStatus(CALL_STATUSES.HAVING_CALL);
          toggleDialPad(true);
          ring();
          notifyInbound(vals[0].number);
        }
      }
    });

    return {
      audio,
      startOutboundCall,
      terminate,
      answer,
      engagingCall
    };
  }
};
</script>

<style lang="scss">
#phone-controller {
  audio {
    display: none;
  }
}
</style>
